/**
 * Google sign-in through the Cognito hosted UI (authorization code + PKCE).
 *
 * Sits alongside the SRP password flow in `auth.ts` rather than replacing it:
 * both end by calling `storeTokens`, so everything downstream — the session
 * check, token refresh, sign-out — is unchanged and unaware of which was used.
 *
 * PKCE rather than a client secret because a PWA cannot keep one. No SDK, for
 * the same reasons `auth.ts` talks to Cognito with `fetch` directly.
 */
import { storeTokens, type Tokens } from './auth.js';

const VERIFIER_KEY = 'intentionalist.pkce-verifier';

const config = () => ({
  domain: (import.meta.env['VITE_COGNITO_DOMAIN'] ?? '').replace(/\/$/, ''),
  clientId: import.meta.env['VITE_USER_POOL_CLIENT_ID'] ?? '',
});

/** Federation is configured only when a hosted UI domain was built in. */
export const isFederationConfigured = (): boolean => config().domain !== '';

const redirectUri = (): string => `${window.location.origin}/callback`;

/* ------------------------------------------------------------------ PKCE */

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

/* ------------------------------------------------------------------ flow */

/** Sends the browser to Google by way of the Cognito hosted UI. */
export async function beginGoogleSignIn(): Promise<void> {
  const { domain, clientId } = config();
  if (domain === '') throw new Error('Google sign-in is not configured for this build');

  const verifier = base64Url(crypto.getRandomValues(new Uint8Array(48)));
  sessionStorage.setItem(VERIFIER_KEY, verifier);

  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: redirectUri(),
    code_challenge: await challengeFor(verifier),
    code_challenge_method: 'S256',
    // Google is the only federated provider, so skip the chooser.
    identity_provider: 'Google',
  });

  window.location.assign(`${domain}/oauth2/authorize?${params}`);
}

/**
 * Redeems the `?code=` on /callback and stores the session.
 *
 * The tokens carry the *linked* user's `sub`, not a new one, because the
 * Google identity was attached to the existing Cognito user with
 * `admin-link-provider-for-user`. Without that link Cognito would mint a
 * separate user and the catalog keyed to the old `sub` would be invisible.
 */
export async function completeGoogleSignIn(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  if (error !== null) throw new Error(params.get('error_description') ?? error);

  const code = params.get('code');
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (code === null) throw new Error('Sign-in did not return an authorization code');
  if (verifier === null) throw new Error('Sign-in session expired — please try again');

  const { domain, clientId } = config();
  const response = await fetch(`${domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: clientId,
      code,
      redirect_uri: redirectUri(),
      code_verifier: verifier,
    }),
  });

  if (!response.ok) throw new Error(`Token request failed (${response.status})`);

  const data = (await response.json()) as {
    access_token: string;
    id_token: string;
    refresh_token: string;
    expires_in: number;
  };

  const tokens: Tokens = {
    accessToken: data.access_token,
    idToken: data.id_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + data.expires_in * 1000,
  };

  storeTokens(tokens);
  sessionStorage.removeItem(VERIFIER_KEY);
}
